import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Home, Sofa, Store, Utensils, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";

const IMG = {
  homeAppliances: "https://static.wixstatic.com/media/11062b_51cf43307fe04a5fbd90f0136b8b2e07~mv2.jpeg",
  inHome: "https://static.wixstatic.com/media/af8bb00e66c944e5bbfb2f85acfb2c63.jpg",
  hypermart: "https://static.wixstatic.com/media/11062b_9c7cdea7e0b6473cb53bc25b853ae308~mv2.jpg",
};

const PhotoCard = ({ img, icon: Icon, name, sub, tag, testid, className, slug }) => {
  const ref = useRef(null);
  const nav = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <div ref={ref} data-testid={testid} onClick={() => nav(`/division/${slug}`)} className={`group relative overflow-hidden bg-konattu-charcoal cursor-pointer ${className}`}>
      <motion.img
        src={img} alt={name} style={{ y }}
        className="absolute inset-0 w-full h-[125%] object-cover grayscale-[35%] group-hover:grayscale-0 scale-105 group-hover:scale-110 transition-all duration-[900ms] ease-out"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-konattu-charcoal via-konattu-charcoal/30 to-transparent" />
      <div className="relative z-10 h-full flex flex-col justify-between p-7 md:p-8">
        <div className="flex items-center justify-between">
          <Icon size={26} strokeWidth={1.25} className="text-konattu-yellow" />
          <span className="font-mono text-[10px] tracking-[0.3em] text-white/60">{tag}</span>
        </div>
        <div>
          <h3 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">{name}</h3>
          <p className="font-body text-sm text-white/70 mt-1.5 max-w-xs">{sub}</p>
          <span className="inline-flex items-center gap-1.5 mt-4 font-mono text-[10px] tracking-[0.25em] text-konattu-yellow opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            EXPLORE <ArrowUpRight size={13} />
          </span>
        </div>
      </div>
    </div>
  );
};

const BrandCard = ({ icon: Icon, name, sub, tag, testid, className, bg, accent, endorse, slug }) => {
  const nav = useNavigate();
  return (
    <div data-testid={testid} onClick={() => nav(`/division/${slug}`)} className={`group relative overflow-hidden p-7 md:p-8 flex flex-col justify-between cursor-pointer ${className}`} style={{ background: bg }}>
      <div className="flex items-center justify-between">
        <Icon size={26} strokeWidth={1.25} style={{ color: accent }} />
        <span className="font-mono text-[10px] tracking-[0.3em] text-white/55">{tag}</span>
      </div>
      <div>
        <h3 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">{name}</h3>
        <p className="font-body text-sm text-white/70 mt-1.5 max-w-xs">{sub}</p>
        <span className="inline-block mt-4 font-mono text-[10px] tracking-[0.25em]" style={{ color: accent }}>{endorse}</span>
      </div>
      <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full opacity-20 blur-2xl transition-opacity duration-500 group-hover:opacity-40" style={{ background: accent }} />
    </div>
  );
};

export const Divisions = () => (
  <section id="divisions" className="bg-konattu-sand py-24 md:py-36" data-testid="divisions-section">
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <div>
          <Reveal><span className="font-mono text-xs tracking-[0.35em] text-konattu-red">/ THE GROUP</span></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-bold text-konattu-charcoal tracking-tight leading-[0.95] text-4xl md:text-6xl">
              One group.<br />Many divisions.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="max-w-sm font-body text-konattu-charcoal/70 text-base leading-relaxed">
            An umbrella of retail formats. Some carry the Konattu name, others have identities of their own, and all share the same promise of trust.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 auto-rows-[300px] gap-4 md:gap-5">
        <Reveal className="md:col-span-4 h-full" delay={0}>
          <PhotoCard slug="home-appliances" testid="division-home-appliances" img={IMG.homeAppliances} icon={Home} tag="KONATTU BRANDED" name="Konattu Home Appliances" sub="National and international brands at reasonable rates, with dependable after sales service." className="h-full" />
        </Reveal>
        <Reveal className="md:col-span-2 h-full" delay={0.08}>
          <PhotoCard slug="inhome" testid="division-inhome" img={IMG.inHome} icon={Sofa} tag="FURNITURE" name="Konattu InHome" sub="Premium furniture that turns a house into a home." className="h-full" />
        </Reveal>
        <Reveal className="md:col-span-2 h-full" delay={0}>
          <BrandCard slug="kitchen-lounges" testid="division-kitchen-lounges" icon={Utensils} tag="ENDORSED BRAND" name="Kitchen Lounges" sub="Crockery & homeware for the kitchen as a gathering space." endorse="by Konattu Group" bg="linear-gradient(150deg,#630933,#A3295C)" accent="#F7B8D6" className="h-full" />
        </Reveal>
        <Reveal className="md:col-span-2 h-full" delay={0.08}>
          <BrandCard slug="emilia" testid="division-emilia" icon={Sparkles} tag="THE BEAUTY HUB" name="Emilia" sub="Cosmetics and ornaments. Beauty and elegance, curated." endorse="by Konattu Group" bg="linear-gradient(150deg,#1a1a1a,#2e2a20)" accent="#CEA768" className="h-full" />
        </Reveal>
        <Reveal className="md:col-span-2 h-full" delay={0.16}>
          <PhotoCard slug="hypermart" testid="division-hypermart" img={IMG.hypermart} icon={Store} tag="HYPERMART" name="Konattu Hypermart" sub="Everyday essentials to appliances, all under one roof." className="h-full" />
        </Reveal>
      </div>
    </div>
  </section>
);

export default Divisions;
